import { useEffect, useRef } from 'react';
import { BACKEND_BASE_URL } from '../../constants/api';
import { userFromJwt } from '../userFromJwt';
import { getSessionId } from './sessionManager';

const TRACK_URL = `${BACKEND_BASE_URL}/api/pita-analytics/track`;

export const PAGE_VIEW_EVENT = 'UserPageViewEvent';
export const PAGE_VIEW_TAGS = ['simplify', 'pageview'];

// The shell itself, not the page being viewed — `path` carries that. The prefix is
// SIMPLIFY_HUB_PLUGIN_PREFIX from simplify-shared, which the shell can't import.
export const SHELL_PLUGIN = 'simplify-hub/shell';

export type UserPageViewEvent = {
  path: string;
  email: string;
  timestamp: string;
};

/** Sends a page view to PITA analytics. Never throws: analytics can't break navigation. */
export const trackPageView = async (path: string, jwt: string): Promise<void> => {
  const payload: UserPageViewEvent = {
    path,
    email: userFromJwt(jwt)?.email ?? '',
    timestamp: new Date().toISOString(),
  };
  try {
    await fetch(TRACK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({
        event: PAGE_VIEW_EVENT,
        payload,
        tags: PAGE_VIEW_TAGS,
        product: SHELL_PLUGIN,
        session: getSessionId(),
      }),
      credentials: 'include',
    });
  } catch {
    // Silent fail
  }
};

/** Tracks a page view each time the shell's route path changes. */
export const usePageViewTracking = (pathname: string, jwt: string | null): void => {
  const lastPath = useRef<string>(undefined);

  useEffect(() => {
    if (!jwt || lastPath.current === pathname) return;
    lastPath.current = pathname;
    void trackPageView(pathname, jwt);
  }, [pathname, jwt]);
};
